import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { AssetRow, FolderRow, JobRow } from "@loumy/contracts";
import { api } from "../api/client";
import { useSession } from "../auth/session";
import {
  ASSET_DRAG_TYPE,
  buildTree,
  FOLDER_DRAG_TYPE,
  hasDragType,
  isSelfOrDescendant,
  JOB_DRAG_TYPE,
  leftDropTarget,
  type FolderTreeNode,
} from "../lib/folderTree";
import {
  useAssets,
  useCreateFolder,
  useFolders,
  useMoveAsset,
  useMoveFolder,
  useMoveJobOutputs,
  useRenameFolder,
  useTrashFolder,
} from "../state/queries";
import { qk } from "../state/queryClient";
import { useUiStore } from "../state/uiStore";
import { getCore } from "../registry/toolRegistry";
import { Badge, cx, Icon, MediaThumb, Spinner } from "../ui";

// §11/D100 Folder-Tree — voll funktional: Anlegen/Umbenennen/Löschen (Hover-Aktionen,
// Inline-Edit, Zwei-Schritt-Löschen) + Drag & Drop. Drop-Ziele: Ordner (Assets UND
// Ordner), Inbox (nur Assets/Jobs → untargeted, §9.13), „Oberste Ebene" (nur Ordner,
// erscheint während eines Folder-Drags). Self-/Descendant-Drops werden clientseitig
// abgelehnt — der harte Zyklus-Schutz bleibt der folders_no_cycle-Trigger (D40).
// INHALTE INLINE (User-Entscheid, ersetzt die kurzlebige /assets-Navigation): mit
// `showContents` (Generate-Sidebar) sind Ordner AUFKLAPPBAR und zeigen ihre Assets
// direkt im Tree als Mini-Zeilen (Thumbnail über die presigned downloadUrls, kind, selbst
// draggable → Tree-interner Move); ein Drop expandiert den Zielordner, damit das Ergebnis
// SOFORT sichtbar ist. Ohne `showContents` (Assets-Browser) bleibt der Tree reine
// Auswahl — dort zeigt das Grid daneben den Inhalt.
// D154: Die Inbox-ZEILE erscheint nur bei `!showContents` (Assets-Browser); in der
// Generate-Sidebar entfällt sie (ihr Inhalt dupliziert die Jobs-Section, s.u.).
// BEWUSSTE EINSCHRÄNKUNG (D100/§13): das VERSCHIEBEN ist DnD-only — Anlegen/Umbenennen/
// Löschen sind voll keyboard-bedienbar (echte Buttons, focus-within), aber ein Keyboard-
// Move-Menü + vollwertige role="tree"-Semantik (inkl. Pfeiltasten-Navigation) sind
// deferred; halbe ARIA-Tree-Rollen ohne komplette Tastatursteuerung wären schlechter
// als keine. Touch/Mobile ebenso ohne DnD (Desktop-first, §11).

type EditingState = { mode: "create"; parentId: string | null } | { mode: "rename"; id: string; initial: string };

interface TreeCtx {
  rows: FolderRow[];
  selectedFolderId: string | null;
  /** Klick auf Ordner/Inbox: Auswahl + (bei showContents) Inhalt auf-/zuklappen. */
  openFolder: (id: string | null) => void;
  /** Reine Auswahl ohne Toggle (z.B. Auswahl-Reset nach Subtree-Trash). */
  selectFolder: (id: string | null) => void;
  showContents: boolean;
  isExpanded: (key: string) => boolean;
  editing: EditingState | null;
  setEditing: (e: EditingState | null) => void;
  confirmTrashId: string | null;
  setConfirmTrashId: (id: string | null) => void;
  draggingFolderId: string | null;
  setDraggingFolderId: (id: string | null) => void;
  dropTarget: string | null;
  setDropTarget: (key: string | null) => void;
  busy: boolean;
  createAt: (parentId: string | null, name: string) => void;
  renameTo: (id: string, name: string) => void;
  trash: (id: string) => void;
  moveFolderTo: (id: string, parentId: string | null) => void;
  moveAssetTo: (assetId: string, folderId: string | null) => void;
  moveJobOutputsTo: (jobId: string, folderId: string | null) => void;
  /** Klick auf eine Inhalts-Zeile: Ursprungs-Job im Tool öffnen (D65, wie die Jobs-Liste). */
  openAsset: (asset: AssetRow) => void;
}

type DropSpec = { kind: "folder"; folderId: string } | { kind: "inbox" } | { kind: "root" };

/** Drop-Handler je Ziel: Inbox nimmt Assets/Jobs, „Oberste Ebene" nur Ordner, Ordner alles. */
function dropProps(ctx: TreeCtx, target: DropSpec) {
  const key = target.kind === "folder" ? target.folderId : target.kind;
  const accepts = (types: readonly string[]): boolean => {
    // Assets UND Job-Ergebnisse (D100) landen in Ordnern oder der Inbox (= untargeted, §9.13).
    if (hasDragType(types, ASSET_DRAG_TYPE) || hasDragType(types, JOB_DRAG_TYPE)) return target.kind !== "root";
    if (hasDragType(types, FOLDER_DRAG_TYPE)) {
      if (target.kind === "inbox") return false;
      if (target.kind === "folder" && ctx.draggingFolderId)
        return !isSelfOrDescendant(ctx.rows, ctx.draggingFolderId, target.folderId);
      return true;
    }
    return false;
  };
  return {
    onDragOver: (e: React.DragEvent) => {
      if (!accepts(e.dataTransfer.types)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (ctx.dropTarget !== key) ctx.setDropTarget(key);
    },
    onDragLeave: (e: React.DragEvent) => {
      // Übergang auf ein Kind-Element derselben Row ≠ verlassen (Codex-P2: Highlight-Flacker).
      if (!leftDropTarget(e.currentTarget, e.relatedTarget)) return;
      if (ctx.dropTarget === key) ctx.setDropTarget(null);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      ctx.setDropTarget(null);
      const folderTarget = target.kind === "folder" ? target.folderId : null;
      const assetId = e.dataTransfer.getData(ASSET_DRAG_TYPE);
      if (assetId && target.kind !== "root") {
        ctx.moveAssetTo(assetId, folderTarget);
        return;
      }
      // D100: Job-Eintrag → ALLE Ergebnis-Assets des Jobs verschieben.
      const jobId = e.dataTransfer.getData(JOB_DRAG_TYPE);
      if (jobId && target.kind !== "root") {
        ctx.moveJobOutputsTo(jobId, folderTarget);
        return;
      }
      const folderId = e.dataTransfer.getData(FOLDER_DRAG_TYPE);
      if (!folderId || target.kind === "inbox") return;
      const parentId = target.kind === "folder" ? target.folderId : null;
      if (parentId !== null && isSelfOrDescendant(ctx.rows, folderId, parentId)) return;
      ctx.moveFolderTo(folderId, parentId);
    },
  };
}

/** Inline-Name-Eingabe: Enter = speichern, Esc = abbrechen, Blur = speichern (wenn nicht leer). */
function NameInput({
  initial,
  depth,
  busy,
  onCommit,
  onCancel,
}: {
  initial: string;
  depth: number;
  busy: boolean;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  // Codex-P1: Enter committet UND das anschließende Blur (Unmount/Fokuswechsel) committet
  // NOCHMAL → doppelter Ordner. `settled` unterdrückt das Blur-Echo; weiteres Tippen
  // (onChange) re-armiert, damit nach einem Mutations-Fehler erneut gespeichert werden kann.
  const settled = useRef(false);
  const commit = () => {
    if (settled.current) return;
    settled.current = true;
    const name = value.trim();
    if (!name || name === initial.trim()) onCancel();
    else onCommit(name);
  };
  return (
    <div style={{ paddingLeft: `${8 + depth * 14}px` }} className="flex items-center gap-2 py-1 pr-2">
      <Icon name="folder" size={16} />
      <input
        // eslint-disable-next-line jsx-a11y/no-autofocus -- bewusst: Inline-Edit, der User hat die Eingabe gerade angefordert
        autoFocus
        value={value}
        disabled={busy}
        maxLength={120}
        onChange={(e) => {
          settled.current = false;
          setValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") {
            settled.current = true;
            onCancel();
          }
        }}
        onBlur={() => commit()}
        placeholder="Ordnername"
        aria-label="Ordnername"
        className="w-full rounded border border-border bg-surface-2 px-1.5 py-0.5 text-sm text-text outline-none focus:border-accent"
      />
    </div>
  );
}

/** Kleine Hover-Aktion (Unterordner/Umbenennen/Löschen) am Ordner-Eintrag. */
function RowAction({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: "plus" | "pencil" | "trash" | "check" | "x";
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cx(
        "rounded p-0.5 transition-colors",
        danger ? "text-muted hover:text-red-400" : "text-muted hover:text-text",
      )}
    >
      <Icon name={icon} size={13} />
    </button>
  );
}

// Leaf-Icon je AssetKind für Zeilen ohne Vorschau-URL (Platzhalter, D43).
const LEAF_ICON: Record<string, "image" | "film" | "video" | "play"> = {
  image: "image",
  spritesheet: "film",
  animation: "film",
  video: "video",
  audio: "play",
};

/**
 * Asset-Zeile INNERHALB eines aufgeklappten Ordners (D100-Inline-Inhalte; D142-Darstellung):
 * links das EINGABE-Bild des Ursprungs-Jobs (`url` = presigned Input-Image; ohne Input —
 * z.B. text→image — fällt der Aufrufer auf die Output-Vorschau zurück, ganz ohne URL ein
 * kind-Icon, D43), rechts daneben der Auto-Titel (`title`, D98; fehlt er, der kind als
 * Fallback). Selbst draggable → Assets lassen sich direkt im Tree zwischen Ordnern/Inbox
 * verschieben. Zusätzlich Drop-DELEGATION (Codex-P2): ein Drop AUF eine Leaf-Zeile wirkt wie
 * ein Drop auf ihren Container — sonst wäre der Bereich zwischen den Ordner-Zeilen ein totes Ziel.
 */
function AssetLeaf({
  asset,
  url,
  title,
  depth,
  drop,
  onOpen,
}: {
  asset: AssetRow;
  url: string | undefined;
  title: string | undefined;
  depth: number;
  drop: ReturnType<typeof dropProps>;
  onOpen: (asset: AssetRow) => void;
}) {
  const openable = asset.source_job_id !== null; // Upload-Assets haben keinen Ursprungs-Job
  // D142: Auto-Titel als Hauptlabel; ohne Titel (Upload / noch laufender Job) der kind als Fallback.
  const label = title ?? asset.kind;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(ASSET_DRAG_TYPE, asset.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      {...drop}
      title={`${label} · ${asset.kind} ${asset.id.slice(0, 8)} — ${openable ? "Klick: Job öffnen; " : "Upload; "}ziehen, um zu verschieben`}
      style={{ paddingLeft: `${8 + depth * 14 + 6}px` }}
      className="flex w-full cursor-grab select-none items-center gap-2 rounded-md py-1 pr-2 text-muted transition-colors hover:bg-surface-2 hover:text-text"
    >
      <button
        type="button"
        // KEIN `disabled` (Codex-P1): ein disabled-Button schluckt Pointer-Events und
        // blockierte damit den Drag-Start über der gesamten Upload-Asset-Zeile — stattdessen
        // guarded onClick + aria-disabled.
        onClick={() => {
          if (openable) onOpen(asset);
        }}
        aria-disabled={openable ? undefined : true}
        aria-label={openable ? `Job öffnen: ${label}` : undefined}
        className={cx(
          "flex min-w-0 flex-1 items-center gap-2 text-left",
          openable ? "cursor-pointer" : "cursor-default",
        )}
      >
        {/* D155: gemeinsame Row-Vorschau (MediaThumb) — 1:1 dieselbe Darstellung wie die Jobs-Liste. */}
        <MediaThumb url={url} icon={LEAF_ICON[asset.kind] ?? "image"} />
        <span className="min-w-0 flex-1 truncate text-[12px]">{label}</span>
      </button>
    </div>
  );
}

/** Inhalt eines aufgeklappten Ordners/der Inbox — lädt erst beim Aufklappen (mount-on-expand). */
function FolderAssets({ folderId, depth, ctx }: { folderId: string | null; depth: number; ctx: TreeCtx }) {
  const assets = useAssets(folderId);
  const rows = assets.data?.assets ?? [];
  const urls = assets.data?.downloadUrls ?? {};
  // D142: Input-Image (Vorrang) + Auto-Titel je Asset; Output-Vorschau bleibt Fallback (text→image).
  const inputUrls = assets.data?.inputImageUrls ?? {};
  const titles = assets.data?.titles ?? {};
  const pad = { paddingLeft: `${8 + depth * 14 + 6}px` };
  // Drop-Delegation an den Container (Codex-P2): Leaf-/Leer-Zeilen nehmen Drops für ihren
  // Ordner bzw. die Inbox an (gleiche Akzeptanz-/Ziel-Logik wie die Container-Zeile).
  const drop = dropProps(ctx, folderId === null ? { kind: "inbox" } : { kind: "folder", folderId });
  if (assets.isLoading)
    return (
      <div style={pad} className="py-1">
        <Spinner size={12} />
      </div>
    );
  if (rows.length === 0)
    return (
      <p {...drop} style={pad} className="py-1 text-[11px] italic text-muted">
        leer
      </p>
    );
  return (
    <>
      {rows.map((a) => (
        <AssetLeaf
          key={a.id}
          asset={a}
          url={inputUrls[a.id] ?? urls[a.id] ?? undefined}
          title={titles[a.id] ?? undefined}
          depth={depth}
          drop={drop}
          onOpen={ctx.openAsset}
        />
      ))}
    </>
  );
}

function FolderItem({ node, depth, ctx }: { node: FolderTreeNode; depth: number; ctx: TreeCtx }) {
  const active = ctx.selectedFolderId === node.id;
  const renaming = ctx.editing?.mode === "rename" && ctx.editing.id === node.id;
  const creatingChild = ctx.editing?.mode === "create" && ctx.editing.parentId === node.id;
  const confirming = ctx.confirmTrashId === node.id;
  const isDropTarget = ctx.dropTarget === node.id;
  // D156: GLOBAL max. EIN Ordner gleichzeitig offen (zeigt seine Inhalte inline) — ctx.isExpanded
  // ist ein Singleton (openFolderId). Kein Auto-Expand nach Kinderzahl (User-Korrektur: das machte
  // jeden Leaf-Ordner auf). Aufklappen = Klick; Subordner sind ohnehin strukturell immer sichtbar.
  const expanded = ctx.showContents && ctx.isExpanded(node.id);

  return (
    <>
      {renaming ? (
        <NameInput
          initial={node.name}
          depth={depth}
          busy={ctx.busy}
          onCommit={(name) => ctx.renameTo(node.id, name)}
          onCancel={() => ctx.setEditing(null)}
        />
      ) : (
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData(FOLDER_DRAG_TYPE, node.id);
            e.dataTransfer.effectAllowed = "move";
            ctx.setDraggingFolderId(node.id);
          }}
          onDragEnd={() => {
            ctx.setDraggingFolderId(null);
            ctx.setDropTarget(null);
          }}
          {...dropProps(ctx, { kind: "folder", folderId: node.id })}
          title={`${node.name} — ziehen, um zu verschieben; Assets/Jobs/Ordner hier ablegen`}
          style={{ paddingLeft: `${8 + depth * 14}px` }}
          className={cx(
            "group flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-sm transition-colors",
            // D156: der GEÖFFNETE Ordner (global max. 1) klebt beim Scrollen seiner Inhalte oben
            // fixiert — OPAKER bg-surface-2 (sonst scheinen die darunter scrollenden Asset-Zeilen
            // durch), z-[5] über die Inhalte, accent-Ring als Marker. Die drei Zweige sind exklusiv
            // (genau EINE bg-Utility greift) — sonst entscheidet die Tailwind-Stylesheet-Reihenfolge,
            // nicht die className-Reihenfolge, welcher Hintergrund gewinnt.
            expanded
              ? "sticky top-0 z-[5] bg-surface-2 text-accent ring-1 ring-inset ring-accent/40"
              : active
                ? "bg-accent/15 text-accent"
                : "text-muted hover:bg-surface-2 hover:text-text",
            isDropTarget && "ring-1 ring-accent bg-accent/10",
          )}
        >
          <button
            type="button"
            onClick={() => ctx.openFolder(node.id)}
            aria-expanded={ctx.showContents ? expanded : undefined}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            {ctx.showContents && <Icon name={expanded ? "chevron-down" : "chevron-right"} size={12} />}
            <Icon name={active ? "folder-open" : "folder"} size={16} />
            <span className="truncate">{node.name}</span>
          </button>
          {confirming ? (
            <span className="flex shrink-0 items-center gap-1 text-[11px] text-red-400">
              Löschen?
              <RowAction icon="check" label="Ordner löschen (Subtree + Inhalte in den Papierkorb)" danger onClick={() => ctx.trash(node.id)} />
              <RowAction icon="x" label="Abbrechen" onClick={() => ctx.setConfirmTrashId(null)} />
            </span>
          ) : (
            <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
              <RowAction icon="plus" label="Unterordner anlegen" onClick={() => ctx.setEditing({ mode: "create", parentId: node.id })} />
              <RowAction icon="pencil" label="Umbenennen" onClick={() => ctx.setEditing({ mode: "rename", id: node.id, initial: node.name })} />
              <RowAction icon="trash" label="Löschen" danger onClick={() => ctx.setConfirmTrashId(node.id)} />
            </span>
          )}
        </div>
      )}
      {creatingChild && (
        <NameInput
          initial=""
          depth={depth + 1}
          busy={ctx.busy}
          onCommit={(name) => ctx.createAt(node.id, name)}
          onCancel={() => ctx.setEditing(null)}
        />
      )}
      {/* D100-Inline-Inhalte: Assets des Ordners direkt im Tree (mount-on-expand). */}
      {expanded && <FolderAssets folderId={node.id} depth={depth + 1} ctx={ctx} />}
      {node.children.map((c) => (
        <FolderItem key={c.id} node={c} depth={depth + 1} ctx={ctx} />
      ))}
    </>
  );
}

export function FolderTree({ showContents = false }: { showContents?: boolean }) {
  const { status } = useSession();
  const { selectedFolderId, selectFolder } = useUiStore();
  const openJob = useUiStore((s) => s.openJob);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const folders = useFolders();
  const rows = folders.data ?? [];
  const tree = buildTree(rows);
  const inboxActive = selectedFolderId === null;
  const signedIn = status === "signed-in";

  const [editing, setEditing] = useState<EditingState | null>(null);
  const [confirmTrashId, setConfirmTrashId] = useState<string | null>(null);
  const [draggingFolderId, setDraggingFolderId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Codex-P2: Sequenz-Token — bei schnellen Klicks auf mehrere Inhalts-Zeilen (Cache-Miss →
  // paralleles api.getJob) darf nur die NEUESTE Anfrage openJob/navigate ausführen.
  const openSeq = useRef(0);
  // D156: GLOBAL max. EIN offener Ordner im ganzen Tree (User-Direktive) — ein Singleton, KEIN Set.
  // Öffnen eines Ordners schließt jeden anderen automatisch; nur mit showContents relevant.
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);

  // Klick auf Ordner/Inbox: Auswahl + (showContents) Inhalt öffnen/schließen. Öffnen ersetzt den
  // bisher offenen Ordner (global 1); erneuter Klick auf den offenen klappt ihn zu. Inhalte leben
  // HIER in der Sidebar (D100). Die Inbox (id=null) wird dort nicht inline gezeigt (D154) → kein Toggle.
  const openFolder = (id: string | null) => {
    selectFolder(id);
    if (showContents && id !== null) setOpenFolderId((cur) => (cur === id ? null : id));
  };
  // Zielordner nach einem Move/Drop offenlegen, damit das verschobene Asset SOFORT sichtbar ist
  // (D100) — schließt dabei einen anderen offenen Ordner. Inbox (null) wird nicht inline gezeigt (D154).
  const revealFolder = (id: string | null) => {
    if (showContents && id !== null) setOpenFolderId(id);
  };

  const createFolder = useCreateFolder();
  const renameFolder = useRenameFolder();
  const trashFolder = useTrashFolder();
  const moveFolder = useMoveFolder();
  const moveAsset = useMoveAsset();
  const moveJobOutputs = useMoveJobOutputs();
  const busy =
    createFolder.isPending ||
    renameFolder.isPending ||
    trashFolder.isPending ||
    moveFolder.isPending ||
    moveAsset.isPending ||
    moveJobOutputs.isPending;

  const onError = (e: unknown) => setError(e instanceof Error ? e.message : "Aktion fehlgeschlagen");

  const ctx: TreeCtx = {
    rows,
    selectedFolderId,
    openFolder,
    selectFolder,
    showContents,
    isExpanded: (id) => openFolderId === id,
    editing,
    setEditing,
    confirmTrashId,
    setConfirmTrashId,
    draggingFolderId,
    setDraggingFolderId,
    dropTarget,
    setDropTarget,
    busy,
    createAt: (parentId, name) => {
      setError(null);
      createFolder.mutate({ name, parentId }, { onError, onSuccess: () => setEditing(null) });
    },
    renameTo: (id, name) => {
      setError(null);
      renameFolder.mutate({ id, name }, { onError, onSuccess: () => setEditing(null) });
    },
    trash: (id) => {
      setError(null);
      trashFolder.mutate(id, {
        onError,
        onSuccess: () => {
          setConfirmTrashId(null);
          // war der ausgewählte Ordner im getrashten Subtree → Auswahl auf Inbox zurück.
          if (selectedFolderId !== null && isSelfOrDescendant(rows, id, selectedFolderId)) selectFolder(null);
          // war der OFFENE Ordner (D156) im getrashten Subtree → schließen (kein verwaister Singleton).
          if (openFolderId !== null && isSelfOrDescendant(rows, id, openFolderId)) setOpenFolderId(null);
        },
      });
    },
    moveFolderTo: (id, parentId) => {
      setError(null);
      moveFolder.mutate({ id, parentId }, { onError });
    },
    moveAssetTo: (assetId, folderId) => {
      setError(null);
      moveAsset.mutate(
        { id: assetId, folderId },
        {
          onError,
          // Zielordner aufklappen — das verschobene Asset ist SOFORT sichtbar (User-Entscheid).
          onSuccess: () => revealFolder(folderId),
        },
      );
    },
    // Inhalts-Zeile angeklickt → Ursprungs-Job im Tool öffnen (D65-Rehydrierung, exakt wie
    // die Jobs-Liste). tool_id kommt aus dem Jobs-Cache (letzte 50) oder per Job-Detail-Read.
    openAsset: (asset) => {
      const jobId = asset.source_job_id;
      if (!jobId) return;
      setError(null);
      const seq = ++openSeq.current;
      void (async () => {
        try {
          const cached = queryClient.getQueryData<JobRow[]>(qk.jobs)?.find((j) => j.id === jobId);
          const toolId = cached?.tool_id ?? (await api.getJob(jobId)).job.tool_id;
          if (seq !== openSeq.current) return; // überholt von einem neueren Klick (Codex-P2)
          const core = getCore(toolId);
          if (!core) {
            setError("Tool nicht mehr verfügbar (D32)");
            return;
          }
          openJob(jobId);
          navigate(`/generate/${core.category}/${core.id}`);
        } catch (e) {
          if (seq === openSeq.current) onError(e);
        }
      })();
    },
    moveJobOutputsTo: (jobId, folderId) => {
      setError(null);
      moveJobOutputs.mutate(
        { id: jobId, folderId },
        {
          onError,
          // moved=0 ist serverseitig legitim (Job ohne Outputs) — dem User aber sagen, warum
          // sich nichts getan hat (z.B. Ergebnis bereits gepurged oder Job ohne Asset-Output).
          onSuccess: (res) => {
            if (res.moved === 0) setError("Dieser Job hat keine (mehr vorhandenen) Ergebnis-Assets.");
            else revealFolder(folderId);
          },
        },
      );
    },
  };

  return (
    <div className="space-y-0.5">
      {/* D154: Die Inbox-ZEILE erscheint NUR im Assets-Browser (showContents=false) als reiner
          Auswahl-/Filter-Eintrag für untargeted Assets (folder_id IS NULL). In der Generate-
          Sidebar (showContents) entfällt sie — dort dupliziert ihr aufgeklappter Inhalt (= die
          untargeted Outputs der letzten Jobs) die „Jobs"-Section direkt darunter (User-Entscheid).
          Das virtuelle folder_id-NULL-Konzept (§9.13/D20) bleibt unberührt: neue Outputs landen
          weiter untargeted bzw. im tool-Default-Ordner (D143), und selectedFolderId=null ist der
          Default-Zustand (kein Ordner hervorgehoben). Im Assets-Browser ist die Zeile reine
          Auswahl (kein Aufklappen — das Grid daneben zeigt den Inhalt), daher kein Chevron/Inline. */}
      {!showContents && (
        <button
          type="button"
          onClick={() => openFolder(null)}
          title="Inbox wird nach 30 Tagen geleert — Assets in einen Ordner ziehen, um sie zu behalten (D25/§9.13)."
          {...dropProps(ctx, { kind: "inbox" })}
          className={cx(
            "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
            inboxActive ? "bg-accent/15 text-accent" : "text-muted hover:bg-surface-2 hover:text-text",
            dropTarget === "inbox" && "ring-1 ring-accent bg-accent/10",
          )}
        >
          <span className="flex items-center gap-2">
            <Icon name="inbox" size={16} />
            Inbox
          </span>
          <Badge tone="warn">
            <Icon name="clock" size={11} /> 30 T.
          </Badge>
        </button>
      )}

      {/* „Oberste Ebene"-Drop-Ziel — nur sichtbar, während ein Ordner gezogen wird. */}
      {draggingFolderId !== null && (
        <div
          {...dropProps(ctx, { kind: "root" })}
          className={cx(
            "flex items-center gap-2 rounded-md border border-dashed border-border px-2 py-1.5 text-[11px] text-muted",
            dropTarget === "root" && "ring-1 ring-accent bg-accent/10 text-text",
          )}
        >
          <Icon name="upload" size={13} /> Auf oberste Ebene
        </div>
      )}

      {!signedIn ? (
        <p className="px-2 py-3 text-[11px] text-muted">Anmelden, um Ordner zu sehen.</p>
      ) : folders.isLoading ? (
        <div className="px-2 py-3">
          <Spinner size={16} />
        </div>
      ) : (
        <>
          {tree.length === 0 && editing === null && (
            <p className="px-2 py-3 text-[11px] text-muted">Noch keine Ordner.</p>
          )}
          {tree.map((n) => (
            <FolderItem key={n.id} node={n} depth={0} ctx={ctx} />
          ))}
          {editing?.mode === "create" && editing.parentId === null && (
            <NameInput
              initial=""
              depth={0}
              busy={busy}
              onCommit={(name) => ctx.createAt(null, name)}
              onCancel={() => setEditing(null)}
            />
          )}
          <button
            type="button"
            onClick={() => setEditing({ mode: "create", parentId: null })}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[12px] text-muted transition-colors hover:bg-surface-2 hover:text-text"
          >
            <Icon name="plus" size={14} /> Neuer Ordner
          </button>
        </>
      )}

      {error && <p className="px-2 py-1 text-[11px] text-red-400">{error}</p>}
    </div>
  );
}
